import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { listImpersonationHistory } from "@/lib/impersonation.functions";

export const Route = createFileRoute("/_authenticated/admin/impersonation")({
  component: ImpersonationHistoryPage,
});

function ImpersonationHistoryPage() {
  const fetch = useServerFn(listImpersonationHistory);
  const { data, isLoading } = useQuery({
    queryKey: ["impersonation-history"],
    queryFn: () => fetch({ data: { limit: 100 } }),
    refetchInterval: 60_000,
  });

  const sessions = data?.sessions ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Impersonation history</h2>
        <p className="text-sm text-muted-foreground">
          Every "view as account" session, with the admin, target, reason, and
          duration. Sessions are read-only; mutations are blocked while active.
        </p>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && sessions.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No impersonation sessions yet.
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <Th>Started</Th>
              <Th>Admin</Th>
              <Th>Target user</Th>
              <Th>Account</Th>
              <Th>Reason</Th>
              <Th>Status</Th>
              <Th>Ended</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sessions.map((s: any) => (
              <tr key={s.id} className="hover:bg-muted/20">
                <Td className="whitespace-nowrap text-muted-foreground">
                  {new Date(s.started_at).toLocaleString()}
                </Td>
                <Td>{s.admin?.full_name ?? s.admin?.email ?? s.admin_user_id}</Td>
                <Td>
                  {s.target_user?.full_name ?? s.target_user?.email ?? s.target_user_id}
                </Td>
                <Td>{s.target_account?.name ?? ","}</Td>
                <Td className="max-w-xs truncate" title={s.reason}>
                  {s.reason}
                </Td>
                <Td>
                  {s.is_active ? (
                    <Badge variant="default">active</Badge>
                  ) : (
                    <Badge variant="outline">{s.end_reason ?? "ended"}</Badge>
                  )}
                </Td>
                <Td className="whitespace-nowrap text-muted-foreground">
                  {s.ended_at ? new Date(s.ended_at).toLocaleString() : ","}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 text-left font-medium">{children}</th>;
}
function Td({
  children,
  className = "",
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <td className={`px-3 py-2 align-top ${className}`} title={title}>
      {children}
    </td>
  );
}
